import { ROLES_ENUM, VALIDATION_ENUM } from "enums";
import { Field, Formik, Form } from "formik";
import { useGetAddress, useStoreAddress, useStoreVat } from "hooks/addresses";
import { useGetMe } from "hooks/auth";
import { useGetCountries, useGetCountriesShort } from "hooks/countries";
import { useEffect, useRef, useState } from "react";
import {
  Button,
  Input,
  PhoneWithPrefix,
  SelectComponent,
  Warning,
} from "shared";
import { UserType } from "types/UsersTypes";
import * as Yup from "yup";
import { isValidPolishNip } from "utils/validation/nipValidation";

interface CompanyAddressProps {
  onClose: () => void;
  user: UserType | null;
}

const CompanyAddress: React.FC<CompanyAddressProps> = ({ onClose, user }) => {
  const { data: countries } = useGetCountries();
  const { data: countriesShort } = useGetCountriesShort();
  const { mutateAsync: storeAddress } = useStoreAddress();
  const { mutateAsync: storeVat } = useStoreVat();
  const { data: authorizedUser } = useGetMe();
  const isAdmin = authorizedUser?.role === ROLES_ENUM.ADMIN;
  const { data } = useGetAddress(isAdmin ? user?.uuid : undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formRef = useRef<any>(null);

  useEffect(() => {
    if (countries && formRef.current && !data?.countryUuid) {
      const poland = countries.find((country) => country.label === "Polska");
      formRef.current.setFieldValue(
        "countryUuid",
        poland?.value || countries[0].value
      );
    }
  }, [countries, data]);

  useEffect(() => {
    if (countriesShort && formRef.current && !data?.nipPrefix) {
      const poland = countriesShort.find((country) => country.label === "PL");
      formRef.current.setFieldValue(
        "nipPrefix",
        poland?.value || countriesShort[0].value
      );
    }
  }, [countriesShort, data]);

  const initialValues = {
    companyName: data?.companyName || "",
    nipPrefix: data?.nipPrefix || "",
    nip: data?.nip || "",
    address: data?.address || "",
    postalCode: data?.postalCode || "",
    city: data?.city || "",
    countryUuid: data?.countryUuid || "",
  };

  const onSubmit = async (values: typeof initialValues) => {
    setIsSubmitting(true);
    const { nipPrefix, nip, ...restValues } = values;
    try {
      if (nip === "" || nip !== data?.nip || nipPrefix !== data?.nipPrefix) {
        await storeVat({
          nip,
          nipPrefix,
          userUuid: isAdmin ? user?.uuid : undefined,
        });
      }

      await storeAddress(
        {
          ...restValues,
          nip,
          nipPrefix,
          type: nip ? "company" : "personal",
          userUuid: isAdmin ? user?.uuid : undefined,
        },
        {
          onSuccess: onClose,
          onSettled: () => {
            setIsSubmitting(false);
          },
        }
      );
    } catch (error: any) {
      if (error?.status === 422) {
        formRef.current?.setFieldError("nip", "   ");
      }
      setIsSubmitting(false);
    }
  };

  const validation = () =>
    Yup.object().shape({
      address: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .max(50)
        .matches(
          /^[^\s].*?[^\s]$/,
          VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE
        ),
      postalCode: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .max(50)
        .matches(
          /^[^\s].*?[^\s]$/,
          VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE
        ),
      city: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .max(50)
        .matches(
          /^[^\s].*?[^\s]$/,
          VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE
        ),
      countryUuid: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .max(50)
        .matches(
          /^[^\s].*?[^\s]$/,
          VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE
        ),
      companyName: Yup.string()
        .max(50)
        .matches(
          /^[^\s].*?[^\s]$/,
          VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE
        )
        .test("companyName-dependent", "  ", function (value) {
          const nip = this.parent.nip?.trim();
          if (!nip) return true;
          return !!value?.trim();
        }),

      nip: Yup.string()
        .max(12)
        .matches(/^\d*$/, "NIP może zawierać tylko cyfry")
        .test("nip-dependent", "  ", function (value) {
          const companyName = this.parent.companyName?.trim();
          if (!companyName) return true;
          return !!value?.trim();
        })
        .test("nip-control", "Niepoprawny numer NIP", function (value) {
          const v = value?.trim();
          if (!v) return true;
          const prefix = this.parent.nipPrefix;
          if (prefix && prefix !== "PL") return true;
          return isValidPolishNip(v);
        }),
      nipPrefix: Yup.string().required(VALIDATION_ENUM.REQUIRED),
    });

  const countriesShortPLOnly = countriesShort
    ? countriesShort.filter((country) => country.label === "PL")
    : [];

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => onSubmit(values)}
      validationSchema={validation()}
      enableReinitialize
      innerRef={formRef}
    >
      {({ errors, values, handleSubmit, submitCount }) => {
        console.log(errors.nip);
        const nipError = () => {
          if (errors.nip === "   ") {
            return (
              <Warning
                className={"edit-personal-data__warning"}
                text={"Niepoprawny numer NIP"}
              />
            );
          } else if (
            errors.nip &&
            submitCount > 0 &&
            values.companyName.length > 0
          ) {
            return (
              <Warning
                className={"edit-personal-data__warning"}
                text={`Uzupełniono nazwę firmy, ale nie dodano numeru NIP. Aby dodać dane firmowe, należy podać również numer NIP.`}
              />
            );
          }
        };

        return (
          <Form className="m-width-100">
            <Field
              type="text"
              id="companyName"
              name="companyName"
              label={"Nazwa firmy"}
              as={Input}
              optional
            />
            <PhoneWithPrefix>
              <Field
                type="number"
                label="NIP"
                name="nipPrefix"
                id="nipPrefix"
                as={SelectComponent}
                options={countriesShortPLOnly}
                optional
              />
              <Field
                name="nip"
                type="text"
                id="nip"
                maxLength="12"
                as={Input}
              />
            </PhoneWithPrefix>
            {nipError()}
            {errors.companyName &&
              !errors.nip &&
              submitCount > 0 &&
              values.nip.length > 0 && (
                <Warning
                  className={"edit-personal-data__warning"}
                  text={`Uzupełniono numer NIP, ale nie dodano nazwy firmy. Aby dodać dane firmowe, należy podać również nazwę firmy.`}
                />
              )}

            <Field
              type="text"
              id="address"
              name="address"
              label={"Adres"}
              as={Input}
            />

            <Field
              type="text"
              id="postalCode"
              name="postalCode"
              label={"Kod pocztowy"}
              as={Input}
            />
            <Field
              type="text"
              id="city"
              name="city"
              label={"Miasto"}
              as={Input}
            />

            <Field
              label="Kraj"
              name="countryUuid"
              id="countryUuid"
              as={SelectComponent}
              options={countries}
              className="select--left"
            />

            <div className="fullscreen-modal__buttons">
              <Button
                label="Anuluj"
                onClick={onClose}
                className="button--stroke button--rounded"
              />
              <Button
                label={"Zapisz zmiany"}
                type="submit"
                onClick={() => null}
                className=" button--rounded button--black"
                isLoading={isSubmitting}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
export default CompanyAddress;
